// My Playlists — personal, employee-owned playlists (PERSONAL scope). Create a playlist,
// add tracks by pasting an Apple Music / Spotify / YouTube Music link, play it back
// (queues playable Apple songs), and open any track in its provider. Provider-neutral:
// we store external references only, never listening data. Auto-generates a colourful
// gradient "cover" per playlist so the grid feels alive without artwork.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ListMusic, Plus, Play, ChevronLeft, ExternalLink, Loader2, Music2 } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { getApiErrorMessage } from '@/lib/apiError';
import { openProviderUrl, isSafeProviderUrl } from '@/lib/providerLinks';
import { usePlayer } from './player/usePlayer';
import { appleRef, resolveSongs } from './lib/playableRef';
import { appleMusicProvider } from './lib/appleMusicProvider';
import type { MusicItem } from './lib/types';
import * as api from '@/services/performanceService';

type Playlist = api.MusicPlaylist;
type PlaylistItem = api.MusicPlaylistItem;

// Deterministic gradient cover from the playlist name — same name, same colours.
const COVERS = [
  'from-orange-500 to-rose-500', 'from-sky-500 to-indigo-500', 'from-emerald-500 to-teal-500',
  'from-violet-500 to-fuchsia-500', 'from-amber-500 to-orange-600', 'from-rose-500 to-purple-600',
  'from-cyan-500 to-blue-600', 'from-lime-500 to-emerald-600',
];
const coverFor = (s: string) => {
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return COVERS[h % COVERS.length];
};
// Apple song id from a canonical URL (for queueing). Non-Apple refs deep-link only.
const PlaylistCover: React.FC<{ name: string; size?: 'sm' | 'lg' }> = ({ name, size = 'sm' }) => (
  <div className={`flex items-center justify-center rounded-xl bg-gradient-to-br ${coverFor(name)} ${size === 'lg' ? 'h-24 w-24' : 'h-full w-full'}`}>
    <ListMusic className={size === 'lg' ? 'h-9 w-9 text-white/90' : 'h-7 w-7 text-white/90'} />
  </div>
);

export const MyPlaylists: React.FC = () => {
  const { playItem, setQueue } = usePlayer();
  const [lists, setLists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ playlist: Playlist; items: PlaylistItem[] } | null>(null);
  const [url, setUrl] = useState('');
  const [trackTitle, setTrackTitle] = useState('');

  const load = () => api.listMyPlaylists().then(setLists).catch(() => setLists([])).finally(() => setLoading(false));
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    api.getMyPlaylist(openId).then(setDetail).catch((e) => { toast.error(getApiErrorMessage(e, 'Could not open the playlist')); setOpenId(null); });
  }, [openId]);

  const guard = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Something went wrong')); }
    finally { setBusy(false); }
  };

  const create = () => guard(async () => {
    if (!newName.trim()) return;
    const p = await api.createMyPlaylist({ name: newName.trim(), description: newDesc.trim() || undefined });
    setNewName(''); setNewDesc(''); setCreating(false);
    await load();
    setOpenId(p.id);
  }, 'Playlist created 🎵');

  const addTrack = () => guard(async () => {
    if (!openId) return;
    if (!isSafeProviderUrl(url.trim())) { toast.error('Paste an Apple Music, Spotify, or YouTube Music link.'); return; }
    await api.addMyPlaylistItem(openId, { url: url.trim(), title: trackTitle.trim() || undefined });
    setUrl(''); setTrackTitle('');
    setDetail(await api.getMyPlaylist(openId));
    await load();
  }, 'Added to playlist');

  // Playing a playlist never leaves KaziPeople: rows resolve to catalog songs (album and
  // playlist links are expanded through the catalog) and queue in order, so the list plays
  // straight through instead of stopping after the first track.
  const [starting, setStarting] = useState(false);
  const playAll = async () => {
    const rows = detail?.items || [];
    if (!rows.length) return;
    setStarting(true);
    try {
      const mk = appleMusicProvider();
      const { songs, partial } = await resolveSongs(rows, { album: (id) => mk.getAlbum(id), playlist: (id) => mk.getPlaylist(id) });
      if (!songs.length) {
        toast.error('Nothing in this playlist can be played here yet.');
        return;
      }
      await setQueue(songs);
      if (partial) toast('Playing the tracks we could load from this playlist.', { icon: '🎵' });
    } finally {
      setStarting(false);
    }
  };

  if (loading) return <div className="p-6 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  // ── Detail view ──
  if (openId && detail) {
    const { playlist, items } = detail;
    return (
      <div className="space-y-5">
        <button onClick={() => setOpenId(null)} className="flex items-center gap-1 text-sm text-primary-600 hover:underline"><ChevronLeft className="h-4 w-4" /> All playlists</button>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <PlaylistCover name={playlist.name} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Playlist</div>
            <h2 className="truncate text-2xl font-bold text-charcoal">{playlist.name}</h2>
            {playlist.description && <p className="text-sm text-muted-foreground">{playlist.description}</p>}
            <p className="mt-0.5 text-xs text-muted-foreground">{items.length} {items.length === 1 ? 'track' : 'tracks'}</p>
          </div>
          {items.length > 0 && (
            <Button size="sm" disabled={starting} onClick={() => void playAll()}>
              {starting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}Play
            </Button>
          )}
        </div>

        {/* Add a track by link */}
        <Card><CardContent className="flex flex-wrap items-end gap-2 pt-4">
          <div className="min-w-[220px] flex-1">
            <label className="text-xs font-medium text-muted-foreground">Add a track or playlist link</label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://music.apple.com/… or Spotify / YouTube Music" className="mt-1.5" />
          </div>
          <Input value={trackTitle} onChange={(e) => setTrackTitle(e.target.value)} placeholder="Title (optional)" className="w-40" />
          <Button size="sm" disabled={busy || !url.trim()} onClick={addTrack}><Plus className="mr-1.5 h-4 w-4" />Add</Button>
        </CardContent></Card>

        <div className="space-y-1.5">
          {items.length === 0 && <p className="text-sm text-muted-foreground">No tracks yet — paste a link above to start building it.</p>}
          {items.map((t, i) => {
            // A row plays in place: a song starts, an album/playlist link starts that
            // album/playlist — all inside KaziPeople. Only an unrecognised link falls back
            // to opening the provider.
            const ref = appleRef(t);
            return (
              <div key={t.id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5">
                <span className="w-5 text-center text-sm font-semibold text-muted-foreground">{i + 1}</span>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted">
                  {t.artwork_url ? <img src={t.artwork_url} alt="" className="h-10 w-10 rounded object-cover" /> : <Music2 className="h-4 w-4 text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground" dir="auto">{t.title || t.canonical_url}</div>
                  <div className="truncate text-xs text-muted-foreground" dir="auto">{t.artist || t.provider?.replace('_', ' ')}</div>
                </div>
                {ref
                  ? <button type="button" aria-label="Play" title="Play" onClick={() => void playItem({ kind: ref.kind, id: ref.id, provider: 'apple_music', title: t.title || '' })}
                      className="rounded-full p-1.5 text-primary-500 hover:bg-muted"><Play className="h-4 w-4" /></button>
                  : <button type="button" aria-label="Open in provider" title="Open in provider" onClick={() => openProviderUrl(t.canonical_url)}
                      className="rounded p-1.5 text-muted-foreground hover:text-foreground"><ExternalLink className="h-4 w-4" /></button>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Grid view ──
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">My Playlists</div>
        {!creating && <Button size="sm" onClick={() => setCreating(true)}><Plus className="mr-1.5 h-4 w-4" />New playlist</Button>}
      </div>

      {creating && (
        <Card><CardContent className="space-y-3 pt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Name</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Deep Focus" className="mt-1.5" autoFocus />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Description (optional)</label>
              <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="What's this playlist for?" className="mt-1.5" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => { setCreating(false); setNewName(''); setNewDesc(''); }}>Cancel</Button>
            <Button size="sm" disabled={busy || !newName.trim()} onClick={create}>Create</Button>
          </div>
        </CardContent></Card>
      )}

      {lists.length === 0 && !creating ? (
        <Card className="border-dashed"><CardContent className="flex flex-col items-start gap-2 py-8">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/10"><ListMusic className="h-6 w-6 text-primary-500" /></span>
          <div className="font-semibold text-foreground">Build your first playlist</div>
          <p className="max-w-md text-sm text-muted-foreground">Collect the tracks you love into your own playlists — private to you. Add songs by pasting Apple Music, Spotify, or YouTube Music links.</p>
          <Button size="sm" className="mt-1" onClick={() => setCreating(true)}><Plus className="mr-1.5 h-4 w-4" />New playlist</Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {lists.map((p) => (
            <button key={p.id} type="button" onClick={() => setOpenId(p.id)}
              className="group rounded-xl border border-border bg-card p-3 text-left transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
              <div className="relative mb-2 aspect-square w-full overflow-hidden rounded-xl">
                <PlaylistCover name={p.name} />
                <span className="absolute bottom-2 right-2 flex h-9 w-9 items-center justify-center rounded-full bg-white text-primary-700 opacity-0 shadow transition group-hover:opacity-100"><Play className="h-4 w-4 translate-x-0.5" /></span>
              </div>
              <div className="truncate text-sm font-semibold text-foreground" dir="auto">{p.name}</div>
              <div className="truncate text-xs text-muted-foreground">{p.item_count ?? 0} {(p.item_count ?? 0) === 1 ? 'track' : 'tracks'}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyPlaylists;
